import React, { useState, useMemo, ChangeEvent, KeyboardEvent } from "react";
import { SecondaryButtons, PrimaryButtons } from "./buttons";
import ScrollContainer from "../core/ScrollContainer";
import Dialog from "./Dialog";
import Badge from "../core/Badge";
import { SUGGESTED_TAG_GROUPS } from "src/constants/listing";
import { PrimaryInput } from "./inputFields";

export interface ManageTagsProps {
  onClose: () => void;
  open: boolean;
  onSaveClick: (tags: string[]) => void;
  tags: string[];
}

const ManageTags: React.FC<ManageTagsProps> = ({
  onClose,
  open,
  onSaveClick,
  tags = [],
}) => {
  const [currentTags, setCurrentTags] = useState<string[]>(tags || []);

  const lowerCasedCurrentTags = useMemo(
    () => currentTags.map((tag) => tag.toLowerCase()),
    [currentTags]
  );

  const [newTagInputText, setNewTagInputText] = useState(``);

  const addTag = (newTagInputText: string) => {
    const sanitizedTag = newTagInputText.replace(/\s/g, "");
    if (sanitizedTag === "") {
      return;
    }

    const lowerCasedTag = sanitizedTag.toLowerCase();
    lowerCasedCurrentTags.find((tag) => tag === lowerCasedTag)
      ? setCurrentTags([...currentTags])
      : setCurrentTags([...currentTags, lowerCasedTag]);
  };

  const removeTag = (tag: string) => {
    setCurrentTags([...currentTags.filter((t) => t !== tag)]);
  };

  return (
    <Dialog
      onOpenChange={onClose}
      isOpen={open}
      title="Tags"
    >
      <div className="max-w-[552px] min-h-[176px] py-4">
        <div className="flex justify-between gap-2 h-9">
          <PrimaryInput
            autoFocus
            value={newTagInputText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setNewTagInputText(e.target.value);
            }}
            onValueChange={setNewTagInputText}
            placeHolder="Enter a tag"
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (e.currentTarget.value) {
                  addTag(e.currentTarget.value);
                  setNewTagInputText("");
                }
              }
            }}
          />
          <SecondaryButtons
            label="Add Tag"
            onClick={() => {
              if (newTagInputText) {
                addTag(newTagInputText);
                setNewTagInputText("");
              }
            }}
          />
        </div>
        <div className="mt-8">
          <p className="font-semibold w-fit whitespace-nowrap text-[14px] text-gray-secondary leading-6">
            Tags
          </p>
          {currentTags.length ? (
            <ScrollContainer>
              <div className="flex flex-wrap gap-2 mt-1 max-h-[200px] pr-[8px] min-[40px]">
                {currentTags.map((tag, index) => {
                  return (
                    <Badge
                      key={`tag ${index}`}
                      className="text-gray-primary cursor-pointer"
                      variant="outline"
                      onClose={() => removeTag(tag)}
                    >
                      {tag}
                    </Badge>
                  );
                })}
              </div>
            </ScrollContainer>
          ) : (
            <p className="text-placeholder italic leading-5 text-[14px] mt-1 min-[40px]">
              No tags added.
            </p>
          )}
        </div>
        <div className="mt-8">
          <p className="font-semibold w-fit whitespace-nowrap text-[14px] text-gray-secondary leading-6">
            Suggested Tags
          </p>
          <div className="flex flex-col gap-2 mt-1">
            {SUGGESTED_TAG_GROUPS.map(
              ({ name, tags: suggestedTags }, index) => {
                const filteredTags = suggestedTags.filter(
                  (suggestedTag) =>
                    !lowerCasedCurrentTags.includes(suggestedTag.toLowerCase())
                );
                return (
                  filteredTags.length && (
                    <div key={name} className="flex flex-wrap gap-2 pr-[8px] w-[90%]">
                      {filteredTags.map((tag, index) => {
                        return (
                          <Badge
                            key={`suggested-tag-${index}`}
                            className="text-gray-primary"
                            variant="outline"
                            onClick={() => {
                              addTag(tag);
                            }}
                          >
                            {tag}
                          </Badge>
                        );
                      })}
                    </div>
                  )
                );
              }
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-between mt-8">
        <SecondaryButtons label="Cancel" onClick={onClose} />
        <PrimaryButtons
          label="Save"
          onClick={() => {
            onSaveClick(currentTags);
          }}
        />
      </div>
    </Dialog>
  );
};

export default ManageTags; 